import React, { useState } from 'react';
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, addMonths, isSameDay, isSameMonth, isBefore, startOfToday } from 'date-fns';
import { es } from 'date-fns/locale';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
interface YearCalendarProps {
  selectedDate: Date | null;
  onSelect: (date: Date) => void;
  /** disable dates before today (client booking) */
  disablePast?: boolean;
}
export function YearCalendar({
  selectedDate,
  onSelect,
  disablePast = true
}: YearCalendarProps) {
  const today = startOfToday();
  const [viewMonth, setViewMonth] = useState<Date>(startOfMonth(selectedDate ?? today));
  const [direction, setDirection] = useState(0);
  const monthStart = startOfMonth(viewMonth);
  const monthEnd = endOfMonth(viewMonth);
  const gridStart = startOfWeek(monthStart, {
    weekStartsOn: 1
  });
  const gridEnd = endOfWeek(monthEnd, {
    weekStartsOn: 1
  });
  const days: Date[] = [];
  let cursor = gridStart;
  while (cursor <= gridEnd) {
    days.push(cursor);
    cursor = addDays(cursor, 1);
  }
  const weekLabels = ['Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sá', 'Do'];
  // When disabling past dates, don't allow navigating before the current month
  const canGoPrev = !disablePast || isBefore(startOfMonth(today), monthStart);
  const goPrev = () => {
    if (!canGoPrev) return;
    setDirection(-1);
    setViewMonth(m => addMonths(m, -1));
  };
  const goNext = () => {
    setDirection(1);
    setViewMonth(m => addMonths(m, 1));
  };
  return <div className="bg-dark-card rounded-2xl border border-white/5 p-4 sm:p-5 w-full" data-id="element-210">
      <div className="flex items-center justify-between mb-4" data-id="element-211">
        <button type="button" aria-label="Mes anterior" disabled={!canGoPrev} onClick={goPrev} className="p-2 text-light-muted hover:text-light hover:bg-white/5 rounded-lg transition-colors disabled:opacity-25 disabled:cursor-not-allowed" data-id="element-212">
          <ChevronLeft size={20} data-id="element-213" />
        </button>
        <h4 className="font-medium text-sm capitalize text-light select-none" data-id="element-214">
          {format(viewMonth, 'MMMM yyyy', {
          locale: es
        })}
        </h4>
        <button type="button" aria-label="Mes siguiente" onClick={goNext} className="p-2 text-light-muted hover:text-light hover:bg-white/5 rounded-lg transition-colors" data-id="element-215">
          <ChevronRight size={20} data-id="element-216" />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1 mb-1" data-id="element-217">
        {weekLabels.map(w => <div key={w} className="text-center text-[10px] font-medium text-light-muted py-1 select-none" data-id="element-218">
            {w}
          </div>)}
      </div>

      <div className="overflow-hidden" data-id="element-219">
        <AnimatePresence mode="wait" initial={false} custom={direction} data-id="element-220">
          <motion.div key={format(viewMonth, 'yyyy-MM')} custom={direction} initial={{
          opacity: 0,
          x: direction * 24
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: direction * -24
        }} transition={{
          duration: 0.18
        }} className="grid grid-cols-7 gap-1" data-id="element-221">
            {days.map(day => {
            const inMonth = isSameMonth(day, viewMonth);
            const isPast = disablePast && isBefore(day, today);
            const isSelected = selectedDate && isSameDay(day, selectedDate);
            const isToday = isSameDay(day, today);
            const disabled = isPast || !inMonth;
            return <button key={day.toISOString()} type="button" disabled={disabled} onClick={() => onSelect(day)} aria-pressed={!!isSelected} aria-label={format(day, "d 'de' MMMM yyyy", {
              locale: es
            })} className={['relative aspect-square rounded-xl text-[13px] font-medium transition-all flex items-center justify-center', !inMonth ? 'text-white/10 cursor-default' : '', isPast && inMonth ? 'text-white/20 cursor-not-allowed' : '', isSelected ? 'bg-gold text-dark font-semibold shadow-md shadow-gold/20' : inMonth && !isPast ? 'text-light hover:bg-white/10 active:scale-95' : '', isToday && !isSelected ? 'ring-1 ring-inset ring-gold/60 text-gold' : ''].join(' ')} data-id="element-222">
                  {format(day, 'd')}
                  {isToday && !isSelected && <span className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-gold" data-id="element-223" />}
                </button>;
          })}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>;
}